import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthConfig, getUploadConfig, ageFrom, fmtDate } from "./shared";

const DOCUMENT_CATEGORY_LABELS = { ordonnance: "Ordonnance", administratif: "Document administratif", patient: "Transmis par le patient" };
const dossierRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F1F5F9" };
const dossierLink = { fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" };

function DossierList({ rows, empty, render }) {
  if (rows === null || rows === undefined) return <div style={{ padding: 14, color: "#94A3B8", fontSize: 13 }}>Chargement…</div>;
  if (rows.length === 0) return <div style={{ padding: 14, color: "#94A3B8", fontSize: 13 }}>{empty}</div>;
  return <div>{rows.map(render)}</div>;
}

/*
 * Patients (cahier des charges §9, §11). Port allégé de PatientsModule
 * (frontend/src/pages/HopitalDashboard.js) sur les routes dédiées
 * /api/lab/patients (backend/routes/lab_patients.py, PR3) : identité +
 * médecin prescripteur, sans dossier clinique (LabPatientCreate n'a ni
 * antécédents ni allergies ni traitements — voir backend/models.py).
 *
 * Volontairement plus simple que le pendant hospitalier : pas d'onglets
 * Parcours/Consultations/Ordonnances/Labo interne/Imagerie/Hospitalisations/
 * Factures (aucun équivalent labo, pas de route /dossier agrégée côté
 * backend) — seulement Identité, Documents, Messages et RGPD, qui ont tous
 * leur route dédiée (backend/routes/lab/patient_documents.py,
 * patient_messaging.py, rgpd.py, PR3b). Pas d'import CSV pour cette première
 * version (patient_import.py n'a pas encore de fork labo).
 */
export default function PatientsModule({ can }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dTab, setDTab] = useState("synthese");
  const [dDocuments, setDDocuments] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docCategory, setDocCategory] = useState("administratif");
  const [docLabel, setDocLabel] = useState("");
  const [docError, setDocError] = useState("");
  const [dMessages, setDMessages] = useState(null);
  const [msgBody, setMsgBody] = useState("");
  const [msgFile, setMsgFile] = useState(null);
  const [msgSending, setMsgSending] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [rgpdBusy, setRgpdBusy] = useState(false);
  const [rgpdError, setRgpdError] = useState("");
  const [rgpdDone, setRgpdDone] = useState("");
  const [anonymizeText, setAnonymizeText] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;
  const empty = { first_name: "", last_name: "", date_of_birth: "", sex: "", national_id: "", phone: "", email: "", address: "", wilaya_code: "", insurance: "", treating_doctor: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
    if (debounced) params.set("search", debounced);
    axios.get(`${API_URL}/api/lab/patients?${params}`, getAuthConfig())
      .then((r) => { setPatients(r.data.patients || []); setTotal(r.data.total ?? (r.data.patients || []).length); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { load(); }, [debounced, page]);
  useEffect(() => {
    const onGlobalSearch = (e) => setSearch(e.detail?.query || "");
    window.addEventListener("tabbibak:searchPatient", onGlobalSearch);
    return () => window.removeEventListener("tabbibak:searchPatient", onGlobalSearch);
  }, []);

  const resetForm = () => { setForm(empty); setEditing(null); setError(""); };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("Nom et prénom obligatoires"); return; }
    setSaving(true); setError("");
    try {
      if (editing) await axios.put(`${API_URL}/api/lab/patients/${editing}`, form, getAuthConfig());
      else await axios.post(`${API_URL}/api/lab/patients`, form, getAuthConfig());
      setShowForm(false); resetForm();
      if (!editing && page !== 0) setPage(0); else load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const fetchFull = async (id) => {
    const r = await axios.get(`${API_URL}/api/lab/patients/${id}`, getAuthConfig());
    return r.data.patient;
  };
  const startEdit = async (p) => {
    try { const full = await fetchFull(p.patient_id); setEditing(p.patient_id); setForm({ ...empty, ...full }); setError(""); setShowForm(true); }
    catch (e) { setError("Impossible de charger le dossier"); }
  };
  const loadDocuments = (patientId) => {
    axios.get(`${API_URL}/api/lab/patients/${patientId}/documents`, getAuthConfig())
      .then((rd) => setDDocuments(rd.data.documents || [])).catch(() => setDDocuments([]));
  };
  const loadMessages = (patientId) => {
    axios.get(`${API_URL}/api/lab/patients/${patientId}/messages`, getAuthConfig())
      .then((rm) => setDMessages(rm.data.messages || [])).catch(() => setDMessages([]));
  };
  const openDetail = async (p) => {
    setDTab("synthese"); setDDocuments(null); setDocError("");
    setDMessages(null); setMsgBody(""); setMsgFile(null); setMsgError("");
    setRgpdError(""); setRgpdDone(""); setAnonymizeText("");
    try {
      const full = await fetchFull(p.patient_id);
      setDetail(full);
      if (can("documents:read")) loadDocuments(p.patient_id);
      if (can("messaging:read")) loadMessages(p.patient_id);
    } catch (e) { setError("Impossible de charger le dossier"); }
  };
  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/lab/patients/${toDelete.patient_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
  };

  const uploadDocument = async (e) => {
    const file = e.target.files && e.target.files[0]; if (!file || !detail) return;
    setDocError(""); setDocUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", docCategory); fd.append("label", docLabel);
    try {
      await axios.post(`${API_URL}/api/lab/patients/${detail.patient_id}/documents`, fd, getUploadConfig());
      setDocLabel(""); loadDocuments(detail.patient_id);
    } catch (er) { setDocError(er.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setDocUploading(false); e.target.value = ""; }
  };
  const viewDocument = async (doc) => {
    try {
      const r = await axios.get(`${API_URL}/api/lab/patients/${detail.patient_id}/documents/${doc.document_id}`, getAuthConfig());
      window.open(r.data.url, "_blank");
    } catch (e) { setDocError("Impossible d'ouvrir le document"); }
  };
  const deleteDocument = async (doc) => {
    try {
      await axios.delete(`${API_URL}/api/lab/patients/${detail.patient_id}/documents/${doc.document_id}`, getAuthConfig());
      loadDocuments(detail.patient_id);
    } catch (e) { setDocError("Erreur lors de la suppression"); }
  };

  const sendMessage = async () => {
    if (!detail || (!msgBody.trim() && !msgFile)) return;
    setMsgError(""); setMsgSending(true);
    const fd = new FormData();
    fd.append("body", msgBody.trim());
    if (msgFile) fd.append("file", msgFile);
    try {
      await axios.post(`${API_URL}/api/lab/patients/${detail.patient_id}/messages`, fd, getUploadConfig());
      setMsgBody(""); setMsgFile(null); loadMessages(detail.patient_id);
    } catch (er) { setMsgError(er.response?.data?.detail || "Erreur lors de l'envoi du message"); }
    finally { setMsgSending(false); }
  };
  const viewMessageAttachment = async (m) => {
    try {
      const r = await axios.get(`${API_URL}/api/lab/patients/${detail.patient_id}/messages/${m.message_id}/attachment`, getAuthConfig());
      window.open(r.data.url, "_blank");
    } catch (e) { setMsgError("Impossible d'ouvrir la pièce jointe"); }
  };

  const exportPatientRgpd = async () => {
    if (!detail) return;
    setRgpdError(""); setRgpdDone(""); setRgpdBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/lab/patients/${detail.patient_id}/rgpd/export`, getAuthConfig());
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `donnees_${detail.patient_id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setRgpdDone("Export téléchargé.");
    } catch (e) { setRgpdError(e.response?.data?.detail || "Erreur lors de l'export"); }
    finally { setRgpdBusy(false); }
  };
  const anonymizePatientRgpd = async () => {
    if (!detail || anonymizeText.trim() !== "ANONYMISER") return;
    setRgpdError(""); setRgpdDone(""); setRgpdBusy(true);
    try {
      await axios.post(`${API_URL}/api/lab/patients/${detail.patient_id}/rgpd/anonymize`,
        { confirm: anonymizeText.trim() }, getAuthConfig());
      setAnonymizeText(""); setRgpdDone("Patient anonymisé.");
      const full = await fetchFull(detail.patient_id);
      setDetail(full); load();
    } catch (e) { setRgpdError(e.response?.data?.detail || "Erreur lors de l'anonymisation"); }
    finally { setRgpdBusy(false); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const taS = { ...iS, minHeight: 56, resize: "vertical" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Patients</h1>
        {can("patients:create") && <button onClick={() => { resetForm(); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau patient</button>}
      </div>

      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le dossier" : "Nouveau patient"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={lab}>Prénom *</label><input style={iS} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><label style={lab}>Nom *</label><input style={iS} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div><label style={lab}>Date de naissance</label><input type="date" style={iS} value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
            <div><label style={lab}>Sexe</label><select style={iS} value={form.sex || ""} onChange={(e) => setForm({ ...form, sex: e.target.value })}><option value="">—</option><option value="M">Masculin</option><option value="F">Féminin</option></select></div>
            <div><label style={lab}>Identifiant national</label><input style={iS} value={form.national_id || ""} onChange={(e) => setForm({ ...form, national_id: e.target.value })} /></div>
            <div><label style={lab}>Téléphone</label><input style={iS} value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label style={lab}>Email</label><input type="email" style={iS} placeholder="Pour les notifications" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div style={{ gridColumn: "span 2" }}><label style={lab}>Adresse</label><input style={iS} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><label style={lab}>Wilaya</label><input style={iS} value={form.wilaya_code || ""} onChange={(e) => setForm({ ...form, wilaya_code: e.target.value })} /></div>
            <div><label style={lab}>Mutuelle / assurance</label><input style={iS} value={form.insurance || ""} onChange={(e) => setForm({ ...form, insurance: e.target.value })} /></div>
            <div><label style={lab}>Médecin prescripteur</label><input style={iS} value={form.treating_doctor || ""} onChange={(e) => setForm({ ...form, treating_doctor: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 12 }}><label style={lab}>Notes</label><textarea style={taS} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input style={{ ...iS, maxWidth: 320 }} placeholder="Rechercher (nom, NIN, téléphone)..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button onClick={() => { setSearch(""); setPage(0); }} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Réinitialiser</button>}
        <span style={{ fontSize: 13, color: "#94A3B8", marginLeft: "auto" }}>
          {total > 0 ? <>{total} patient{total > 1 ? "s" : ""}{search ? " trouvé" + (total > 1 ? "s" : "") : ""}{total > PAGE_SIZE ? ` · page ${page + 1} sur ${Math.ceil(total / PAGE_SIZE)}` : ""}</> : "Aucun patient"}
        </span>
      </div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : patients.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
            {search ? `Aucun patient ne correspond à « ${search} ».` : "Aucun patient. Cliquez sur « Nouveau patient »."}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              {["Nom", "Naissance", "Sexe", "Téléphone", "NIN", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {patients.map((p) => {
                const age = ageFrom(p.date_of_birth);
                return (
                  <tr key={p.patient_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{p.first_name} {p.last_name}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{p.date_of_birth || "—"}{age != null ? ` (${age} ans)` : ""}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{p.sex === "M" ? "M" : p.sex === "F" ? "F" : "—"}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{p.phone || "—"}</td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#94A3B8", fontFamily: "monospace" }}>{p.national_id || "—"}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ display: "flex", gap: 12 }}>
                        <button onClick={() => openDetail(p)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Dossier</button>
                        {can("patients:update") && <button onClick={() => startEdit(p)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>}
                        {can("patients:delete") && <button onClick={() => setToDelete(p)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 16 }}>
          <button onClick={() => setPage((n) => Math.max(0, n - 1))} disabled={page === 0}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: page === 0 ? "#F1F5F9" : "#fff", color: page === 0 ? "#CBD5E1" : "#334155", fontSize: 13, cursor: page === 0 ? "default" : "pointer" }}>← Précédent</button>
          <span style={{ fontSize: 13, color: "#64748B" }}>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} sur {total}</span>
          <button onClick={() => setPage((n) => n + 1)} disabled={(page + 1) * PAGE_SIZE >= total}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: (page + 1) * PAGE_SIZE >= total ? "#F1F5F9" : "#fff", color: (page + 1) * PAGE_SIZE >= total ? "#CBD5E1" : "#334155", fontSize: 13, cursor: (page + 1) * PAGE_SIZE >= total ? "default" : "pointer" }}>Suivant →</button>
        </div>
      )}

      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 720, padding: 24, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, color: "#0F172A" }}>{detail.first_name} {detail.last_name}</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>
                  {ageFrom(detail.date_of_birth) != null ? `${ageFrom(detail.date_of_birth)} ans` : ""}
                  {detail.sex ? ` · ${detail.sex === "M" ? "Masculin" : "Féminin"}` : ""}
                </div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 16, marginBottom: 16, flexWrap: "wrap", borderBottom: "1px solid #E2E8F0", paddingBottom: 10 }}>
              {[["synthese", "Synthèse"], ...(can("documents:read") ? [["documents", `Documents${dDocuments?.length ? ` (${dDocuments.length})` : ""}`]] : []), ...(can("messaging:read") ? [["messages", `Messages${dMessages?.length ? ` (${dMessages.length})` : ""}`]] : []), ...(can("rgpd:export") ? [["rgpd", "RGPD"]] : [])].map(([id, lbl]) => (
                <button key={id} onClick={() => setDTab(id)} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 8, cursor: "pointer", border: dTab === id ? "1px solid #185FA5" : "1px solid #E2E8F0", background: dTab === id ? "#185FA5" : "#fff", color: dTab === id ? "#fff" : "#475569" }}>{lbl}</button>
              ))}
            </div>

            {dTab === "synthese" && (
              <div>
                <div style={{ background: "#fff", border: "1px solid #F1F5F9", borderRadius: 10 }}>
                  {[["Date de naissance", detail.date_of_birth ? `${detail.date_of_birth}${ageFrom(detail.date_of_birth) != null ? ` (${ageFrom(detail.date_of_birth)} ans)` : ""}` : null],
                    ["Identifiant national", detail.national_id], ["Téléphone", detail.phone], ["Email", detail.email], ["Adresse", detail.address],
                    ["Wilaya", detail.wilaya_code], ["Mutuelle / assurance", detail.insurance], ["Médecin prescripteur", detail.treating_doctor], ["Notes", detail.notes]]
                    .filter(([, v]) => v)
                    .map(([l, v]) => (
                      <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                        <span style={{ fontSize: 13, color: "#64748B" }}>{l}</span>
                        <span style={{ fontSize: 14, color: "#0F172A", fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {dTab === "documents" && can("documents:read") && (
              <div>
                {docError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{docError}</div>}
                {can("documents:write") && (
                  <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
                        <option value="ordonnance">Ordonnance</option>
                        <option value="administratif">Document administratif</option>
                        <option value="patient">Transmis par le patient</option>
                      </select>
                      <input placeholder="Libellé (optionnel)" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }} />
                    </div>
                    <label style={{ display: "inline-block", fontSize: 13, color: "#185FA5", cursor: "pointer", border: "1px dashed #B5D4F4", borderRadius: 8, padding: "8px 14px" }}>
                      {docUploading ? "Envoi en cours..." : "+ Ajouter un document (JPEG, PNG, PDF - max 10 Mo)"}
                      <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={uploadDocument} style={{ display: "none" }} disabled={docUploading} />
                    </label>
                  </div>
                )}
                <DossierList rows={dDocuments} empty="Aucun document"
                  render={(doc) => (
                    <div key={doc.document_id} style={dossierRow}>
                      <div>
                        <div style={{ fontWeight: 500, color: "#0F172A" }}>{doc.label || doc.filename}</div>
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>
                          {DOCUMENT_CATEGORY_LABELS[doc.category] || doc.category} · {fmtDate(doc.uploaded_at)}
                          {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ""} · {(doc.size / 1024 / 1024).toFixed(2)} Mo
                        </div>
                      </div>
                      <span style={{ display: "flex", gap: 12 }}>
                        <button onClick={() => viewDocument(doc)} style={dossierLink}>Voir</button>
                        {can("documents:write") && <button onClick={() => deleteDocument(doc)} style={{ ...dossierLink, color: "#A32D2D" }}>Supprimer</button>}
                      </span>
                    </div>
                  )} />
              </div>
            )}

            {dTab === "messages" && can("messaging:read") && (
              <div>
                {msgError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{msgError}</div>}
                <DossierList rows={dMessages} empty="Aucun message"
                  render={(m) => (
                    <div key={m.message_id} style={{ ...dossierRow, alignItems: "flex-start", flexDirection: m.sender === "staff" ? "row-reverse" : "row" }}>
                      <div style={{ maxWidth: "78%" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "baseline", justifyContent: m.sender === "staff" ? "flex-end" : "flex-start" }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: m.sender === "staff" ? "#185FA5" : "#0F172A" }}>{m.sender === "staff" ? (m.sender_name || "Établissement") : (m.sender_name || "Patient")}</span>
                          <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDate(m.created_at)}</span>
                        </div>
                        {m.body && <div style={{ fontSize: 13, color: "#334155", marginTop: 4, whiteSpace: "pre-wrap", textAlign: m.sender === "staff" ? "right" : "left" }}>{m.body}</div>}
                        {m.attachment && <div style={{ marginTop: 4, textAlign: m.sender === "staff" ? "right" : "left" }}><button onClick={() => viewMessageAttachment(m)} style={dossierLink}>📎 {m.attachment.filename}</button></div>}
                      </div>
                    </div>
                  )} />
                {can("messaging:write") && (
                  <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginTop: 16 }}>
                    <textarea placeholder="Écrire un message au patient..." value={msgBody} onChange={(e) => setMsgBody(e.target.value)}
                      style={{ width: "100%", minHeight: 64, padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                      <label style={{ fontSize: 12, color: "#185FA5", cursor: "pointer" }}>
                        {msgFile ? `📎 ${msgFile.name}` : "+ Pièce jointe (JPEG, PNG, PDF)"}
                        <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setMsgFile(e.target.files?.[0] || null)} style={{ display: "none" }} />
                      </label>
                      <button onClick={sendMessage} disabled={msgSending || (!msgBody.trim() && !msgFile)}
                        style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (msgSending || (!msgBody.trim() && !msgFile)) ? 0.6 : 1 }}>
                        {msgSending ? "Envoi..." : "Envoyer"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {dTab === "rgpd" && can("rgpd:export") && (
              <div>
                {rgpdError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{rgpdError}</div>}
                {rgpdDone && <div style={{ background: "#EAF7EC", border: "1px solid #BFE6C4", color: "#276B32", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{rgpdDone}</div>}
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, color: "#0F172A", marginBottom: 4 }}>Exporter les données du patient</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>Télécharge un fichier contenant l'identité, les rendez-vous, l'historique d'accueil, les messages et la liste des documents de ce patient (droit d'accès, section 33).</div>
                  <button onClick={exportPatientRgpd} disabled={rgpdBusy} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: rgpdBusy ? 0.6 : 1 }}>
                    {rgpdBusy ? "..." : "Exporter (JSON)"}
                  </button>
                </div>
                {can("rgpd:anonymize") && !detail.anonymized && (
                  <div style={{ background: "#FEF6F6", border: "1px solid #F3C9C9", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontWeight: 600, color: "#A32D2D", marginBottom: 4 }}>Anonymiser ce patient</div>
                    <div style={{ fontSize: 12, color: "#7A3131", marginBottom: 10 }}>
                      Action irréversible. Le nom, les coordonnées et le lien du portail patient seront définitivement effacés et remplacés dans toutes les données du laboratoire (rendez-vous, accueil, messagerie). Aucun rendez-vous n'est annulé.
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input placeholder='Taper "ANONYMISER" pour confirmer' value={anonymizeText} onChange={(e) => setAnonymizeText(e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid #F3C9C9", borderRadius: 8, fontSize: 13, minWidth: 220 }} />
                      <button onClick={anonymizePatientRgpd} disabled={rgpdBusy || anonymizeText.trim() !== "ANONYMISER"}
                        style={{ padding: "8px 18px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (rgpdBusy || anonymizeText.trim() !== "ANONYMISER") ? 0.5 : 1 }}>
                        {rgpdBusy ? "..." : "Anonymiser définitivement"}
                      </button>
                    </div>
                  </div>
                )}
                {detail.anonymized && (
                  <div style={{ fontSize: 12, color: "#64748B" }}>Ce patient a été anonymisé{detail.anonymized_at ? ` le ${fmtDate(detail.anonymized_at)}` : ""}.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer ce patient ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20, lineHeight: 1.5 }}>Le dossier de <b>{toDelete.first_name} {toDelete.last_name}</b> sera supprimé. Action irréversible.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
